import type { PawnColour } from '@root/lib/pawnColour';
import type { LudoRollOutcome, LudoTile } from '@root/store/types/round';

export type ReactToPhaserEvent =
  // Full four-sided board, then the zoom down to the player's own lane.
  | { type: 'PLAY_INTRO'; colour: PawnColour }
  // Skip the intro and show the lane straight away, e.g. on reconnect.
  | { type: 'SHOW_LANE'; colour: PawnColour }
  | { type: 'SET_COLOUR'; colour: PawnColour }
  // Tiles 1-11 as the server generated them for this run.
  | { type: 'SET_BOARD'; tiles: LudoTile[] }
  // Place the pawn with no animation. Restoring a run, not playing it.
  | { type: 'SET_POSITION'; position: number }
  | {
      type: 'PLAY_ROLL';
      dice: number;
      from: number;
      to: number;
      outcome: LudoRollOutcome;
      payout: number;
    }
  | { type: 'FLASH'; duration?: number }
  | { type: 'SET_BG_COLOR'; color: string };

export type PhaserToReactEvent =
  | { type: 'SCENE_READY'; scene: string }
  | { type: 'SCENE_ERROR'; scene: string; error: string }
  | { type: 'LOAD_PROGRESS'; value: number }
  | { type: 'LOAD_COMPLETE' }
  | { type: 'INTRO_COMPLETE' }
  // The pawn has finished moving and the outcome has played out, so the
  // controls can unlock. Always fires, including for a wasted overshoot.
  | { type: 'ROLL_COMPLETE'; position: number; outcome: LudoRollOutcome };

type Channel = 'r2p' | 'p2r';

class TypedBus {
  private target = new EventTarget();

  emit(channel: Channel, payload: unknown): void {
    this.target.dispatchEvent(new CustomEvent(channel, { detail: payload }));
  }

  on<T>(channel: Channel, handler: (_payload: T) => void): () => void {
    const listener = (e: Event) => handler((e as CustomEvent).detail as T);
    this.target.addEventListener(channel, listener);
    return () => this.target.removeEventListener(channel, listener);
  }
}

const bus = new TypedBus();

export const phaserBridge = {
  sendToPhaser(event: ReactToPhaserEvent): void {
    bus.emit('r2p', event);
  },
  onReactCommand(handler: (_event: ReactToPhaserEvent) => void): () => void {
    return bus.on('r2p', handler);
  },
  sendToReact(event: PhaserToReactEvent): void {
    bus.emit('p2r', event);
  },
  onPhaserEvent(handler: (_event: PhaserToReactEvent) => void): () => void {
    return bus.on('p2r', handler);
  },
};
